import React from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { useDroppable } from "@dnd-kit/core";
import { FormattedMessage } from "react-intl";
import { colors, fontSize } from "style";
import { Spinner } from "components/Loader";
import ApplicantBoardCard from "./ApplicantBoardCard";

/**
 * ApplicantBoardColumn
 *
 * @param {Object} column
 * @param {Array} applicants
 * @param {Boolean} loading
 */
const ApplicantBoardColumn = ({ column, applicants, loading, ...props }) => {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });

  return (
    <section ref={setNodeRef} css={styles.column(isOver)}>
      <header css={styles.header}>
        <span>
          <FormattedMessage id={column.title} defaultMessage={column.defaultTitle} />
        </span>
        <span css={styles.count}>{applicants.length}</span>
      </header>

      <div css={styles.body}>
        {loading ? (
          <div css={styles.loading}>
            <Spinner />
          </div>
        ) : applicants.length ? (
          applicants.map((applicant) => <ApplicantBoardCard key={applicant.id} applicant={applicant} {...props} />)
        ) : (
          <div css={styles.empty}>
            <FormattedMessage id="Missions.Applicants.Board.Empty" defaultMessage="Drop applicants here" />
          </div>
        )}
      </div>
    </section>
  );
};

const styles = {
  column: (isOver) => css`
    min-width: calc((100% - 5rem) / 6);
    max-height: 90vh;
    width: calc((100% - 5rem) / 6);
    height: 90vh;
    border-radius: 1rem;
    background: ${isOver ? colors.purpleRainLight5 : colors.grayAnatomyLight7};
    border: 1px solid ${isOver ? colors.purpleRainLight2 : colors.grayAnatomyLight4};
    transition: 0.15s ease;
    overflow-y: scroll;
  `,
  header: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    color: ${colors.grayAnatomyBase};
    font-size: ${fontSize.normal};
    font-weight: 600;
  `,
  count: css`
    min-width: 1.75rem;
    height: 1.75rem;
    padding: 0 0.5rem;
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${colors.white};
    color: ${colors.grayAnatomyBase};
    font-size: ${fontSize.small};
  `,
  body: css`
    min-height: 18rem;
    padding: 0 0.75rem 0.75rem;
  `,
  loading: css`
    display: flex;
    justify-content: center;
    padding: 2rem 0;
  `,
  empty: css`
    border: 1px dashed ${colors.grayAnatomyLight2};
    border-radius: 0.75rem;
    padding: 1rem;
    color: ${colors.grayAnatomyBase};
    text-align: center;
    font-size: ${fontSize.small};
  `,
};

ApplicantBoardColumn.propTypes = {
  column: PropTypes.object.isRequired,
  applicants: PropTypes.array.isRequired,
  loading: PropTypes.bool,
};

ApplicantBoardColumn.defaultProps = {
  loading: false,
};

export default ApplicantBoardColumn;
